/* @jsx h */
import { defineComponent, h, ref, computed, onMounted } from "vue";
import {
  SelectableListComboBox,
  useLocalization,
  type SelectableItem,
} from "@lemmonhq/uikit";
import type { CoinEarnRule } from "@lemmonhq/types";
import type { Category, Product } from "@lemmonhq/types";
import { api } from "@app/data/index";
import { useAuth } from "@app/vue/providers/Auth";

/** Products the merchant can attach a `product` rule to. */
const ProductPicker = defineComponent<{
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}>({
  name: "CoinEarnProductPicker",
  props: ["selectedIds", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const products = ref<Product[]>([]);
    const loading = ref(false);

    onMounted(async () => {
      loading.value = true;
      try {
        products.value = await api.getProducts();
      } catch (error) {
        console.error("Error loading products:", error);
      } finally {
        loading.value = false;
      }
    });

    const items = computed((): SelectableItem[] =>
      products.value.map((product) => {
        const currency = auth.user.value?.restaurant.currency || "CHF";
        const price = parseFloat(product.price);
        return {
          id: product.id,
          name: product.name,
          subtitle: isNaN(price) ? undefined : `${currency} ${price.toFixed(2)}`,
        };
      }),
    );

    return () => (
      <SelectableListComboBox
        items={items.value}
        selectedIds={props.selectedIds}
        onSelectionChange={props.onChange}
        loading={loading.value}
        placeholder={t("promotions.coins.rule_products_search")}
        emptyMessage={t("promotions.coins.rule_products_empty")}
        selectionLabel={t("promotions.coins.rule_products_selected")}
        defaultShowSelected={props.selectedIds.length > 0}
      />
    );
  },
});

/**
 * Categories come off the services payload, the same source
 * `ProductCategoryRequirementEditor` uses — there is no categories list API.
 */
const CategoryPicker = defineComponent<{
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}>({
  name: "CoinEarnCategoryPicker",
  props: ["selectedIds", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const categories = ref<Category[]>([]);
    const loading = ref(false);

    onMounted(async () => {
      loading.value = true;
      try {
        const services = await api.getServices();
        categories.value = services
          .map((service) => service.categories || [])
          .flat() as Category[];
      } catch (error) {
        console.error("Error loading categories:", error);
      } finally {
        loading.value = false;
      }
    });

    const items = computed((): SelectableItem[] =>
      categories.value.map((category) => ({
        id: category.id,
        name: category.name,
      })),
    );

    return () => (
      <SelectableListComboBox
        items={items.value}
        selectedIds={props.selectedIds}
        onSelectionChange={props.onChange}
        loading={loading.value}
        placeholder={t("promotions.coins.rule_categories_search")}
        emptyMessage={t("promotions.coins.rule_categories_empty")}
        selectionLabel={t("promotions.coins.rule_categories_selected")}
        defaultShowSelected={props.selectedIds.length > 0}
      />
    );
  },
});

const NumberField = defineComponent<{
  label: string;
  hint?: string;
  value: number;
  min?: number;
  step?: number;
  onChange: (value: number) => void;
}>({
  name: "CoinEarnNumberField",
  props: ["label", "hint", "value", "min", "step", "onChange"],
  setup(props) {
    return () => (
      <div class="form-control">
        <label class="label py-1">
          <span class="label-text font-medium">{props.label}</span>
        </label>
        <input
          type="number"
          value={props.value}
          min={props.min ?? 1}
          step={props.step ?? 1}
          onInput={(e) => {
            const raw = (e.target as HTMLInputElement).value;
            const parsed = props.step && props.step < 1 ? parseFloat(raw) : parseInt(raw);
            props.onChange(Number.isFinite(parsed) ? parsed : 0);
          }}
          class="input input-bordered input-sm focus:input-primary w-full"
        />
        {props.hint && (
          <label class="label py-1">
            <span class="label-text-alt text-base-content/50">{props.hint}</span>
          </label>
        )}
      </div>
    );
  },
});

/**
 * A worked example of the spend rule, because "1 per 10" reads like a rate and
 * is not one: the basis is floored per payment and the remainder is dropped
 * rather than carried over. See docs/plans/loyalty/earn_capture.md §4.
 */
const SpendPreview = defineComponent<{
  coinsPerUnit: number;
  unitAmount: number;
  currency: string;
}>({
  name: "CoinEarnSpendPreview",
  props: ["coinsPerUnit", "unitAmount", "currency"],
  setup(props) {
    const { t } = useLocalization();

    return () => {
      if (!(props.unitAmount > 0) || !(props.coinsPerUnit > 0)) return null;

      // A sample that deliberately does not divide evenly, so the dropped
      // remainder is visible.
      const sample = Math.round(props.unitAmount * 2.4 * 100) / 100;
      const units = Math.floor(sample / props.unitAmount);
      const earned = units * props.coinsPerUnit;
      const remainder =
        Math.round((sample - units * props.unitAmount) * 100) / 100;

      const money = (amount: number) =>
        `${props.currency} ${amount.toFixed(2)}`;

      // The sentence says "coins" rather than the merchant's own singular label:
      // t() has no pluralisation, and the field label directly above already
      // names the unit, so "earns 2 coins" beats "earns 2 stamp".
      return (
        <p class="text-xs text-base-content/50 bg-base-200 rounded-lg px-3 py-2">
          {remainder > 0
            ? t("promotions.coins.rule_spend_preview", [
                money(sample),
                earned,
                money(remainder),
              ])
            : t("promotions.coins.rule_spend_preview_exact", [
                money(sample),
                earned,
              ])}
        </p>
      );
    };
  },
});

export type EarnRuleFieldsProps = {
  value: CoinEarnRule;
  /** Singular coin noun — "stamp", "point" — used in the field labels. */
  coinLabel: string;
  onChange: (rule: CoinEarnRule) => void;
};

/** The per-variant body of one earn rule. The card around it lives in EarnRulesEditor. */
export const EarnRuleFields = defineComponent<EarnRuleFieldsProps>({
  name: "EarnRuleFields",
  props: ["value", "coinLabel", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const currency = computed(
      () => auth.user.value?.restaurant.currency || "CHF",
    );

    const patch = (fields: Partial<CoinEarnRule>) =>
      props.onChange({ ...props.value, ...fields } as CoinEarnRule);

    return () => {
      const rule = props.value;

      if (rule.type === "spend") {
        return (
          <div class="space-y-3">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <NumberField
                label={t("promotions.coins.rule_spend_coins", [props.coinLabel])}
                value={rule.coins_per_unit}
                onChange={(v) => patch({ coins_per_unit: v })}
              />
              <NumberField
                label={t("promotions.coins.rule_spend_unit", [currency.value])}
                value={rule.unit_amount}
                step={0.01}
                onChange={(v) => patch({ unit_amount: v })}
              />
            </div>
            <SpendPreview
              coinsPerUnit={rule.coins_per_unit}
              unitAmount={rule.unit_amount}
              currency={currency.value}
            />
          </div>
        );
      }

      if (rule.type === "product") {
        return (
          <div class="space-y-3">
            <NumberField
              label={t("promotions.coins.rule_product_coins", [props.coinLabel])}
              value={rule.coins}
              onChange={(v) => patch({ coins: v })}
            />
            <ProductPicker
              selectedIds={rule.product_ids}
              onChange={(ids) => patch({ product_ids: ids })}
            />
          </div>
        );
      }

      if (rule.type === "category") {
        return (
          <div class="space-y-3">
            <NumberField
              label={t("promotions.coins.rule_category_coins", [props.coinLabel])}
              value={rule.coins}
              onChange={(v) => patch({ coins: v })}
            />
            <CategoryPicker
              selectedIds={rule.category_ids}
              onChange={(ids) => patch({ category_ids: ids })}
            />
          </div>
        );
      }

      if (rule.type === "visit") {
        return (
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <NumberField
              label={t("promotions.coins.rule_visit_coins", [props.coinLabel])}
              value={rule.coins}
              onChange={(v) => patch({ coins: v })}
            />
            <NumberField
              label={t("promotions.coins.rule_visit_max")}
              hint={t("promotions.coins.rule_visit_hint")}
              value={rule.max_per_day}
              onChange={(v) => patch({ max_per_day: v })}
            />
          </div>
        );
      }

      // `referral` is in the type union but has no editor until the fraud
      // controls land — see docs/plans/loyalty/referral_earn_rule.md.
      return (
        <p class="text-sm text-base-content/50">
          {t("promotions.coins.rule_referral_pending")}
        </p>
      );
    };
  },
});
